import { useMutation } from "@tanstack/react-query";
import { Alert, Button, Table } from "react-bootstrap";
import Loading from "../../Loading";
import { serverError, useAuthenticatedFetch } from "../../lib/api";
import { processFileResponseOrThrow } from "../../lib/file";
import { useQueryHouseholdGroupStatements } from "./lib";

/**
 * The api's refusal text when it sent one, else the app's generic text. No
 * other words are added to the api's text.
 */
const failureText = (err: unknown): string => {
  const message = serverError(err)?.body?.message;
  return typeof message === "string" ? message : "An error occurred";
};

/**
 * Downloads the group billing statement of one billing report. Each row has
 * its own component because a hook cannot run in a loop over rows.
 */
const StatementDownloadButton = ({
  billingReportId,
  householdGroupId,
}: {
  billingReportId: number;
  householdGroupId: number;
}) => {
  const url = `/billing/reports/${billingReportId}/household-groups/${householdGroupId}/statement`;
  // Not useAuthenticatedFileFetch: it resolves to undefined, which TanStack Query 5 treats as an error.
  const { refetch: downloadStatement, error } = useAuthenticatedFetch(
    url,
    {},
    { enabled: false, retry: false },
    [url],
    async (response) => {
      await processFileResponseOrThrow(response);
      return true;
    },
  );

  const downloadStatementMutation = useMutation({
    mutationFn: async () => {
      await downloadStatement();
    },
  });

  return (
    <>
      {error == null ? null : (
        <Alert variant="danger">{failureText(error)}</Alert>
      )}
      <Button
        onClick={() => downloadStatementMutation.mutate()}
        disabled={downloadStatementMutation.isPending}
      >
        Download Statement
      </Button>
    </>
  );
};

/**
 * The group billing statements of a household group, one per billing report
 * that holds one, each with its download. The statement is not a `report`
 * row, so no row links to the Reports pages.
 */
const HouseholdGroupStatements = ({
  householdGroupId,
}: {
  householdGroupId: number;
}) => {
  const {
    isPending,
    error,
    data: statements,
  } = useQueryHouseholdGroupStatements(householdGroupId);

  return (
    <>
      <h3>Group Billing Statements</h3>
      {isPending ? (
        <Loading />
      ) : error != null ? (
        <Alert variant="danger">{failureText(error)}</Alert>
      ) : (statements ?? []).length === 0 ? (
        <Alert>No group billing statements found</Alert>
      ) : (
        <Table>
          <thead>
            <tr>
              <th>Billing Date</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(statements ?? []).map((statement) => (
              <tr key={statement.billingReportId}>
                <td>{statement.endDate}</td>
                <td>
                  <StatementDownloadButton
                    billingReportId={statement.billingReportId}
                    householdGroupId={householdGroupId}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </>
  );
};

export default HouseholdGroupStatements;
